<template>
  <div id="generated-travel-book" class="h-100">
    <v-loader v-if="loadingTravelBook" />

    <div v-else-if="show404Error" class="error-container d-flex align-items-center justify-content-center h-100">
      <div class="p-16 py-lg-24 px-lg-32">
        <div class="title fw-bold">{{ $t("general.errors.error404") }}</div>
        <div class="text-xl mb-16">{{ $t("general.errors.pageNotFound") }}</div>
        <div>{{ $t("general.errors.pageNotFoundText") }}</div>
      </div>
    </div>

    <template v-else>
      <v-travel-book-page-header v-if="!!tour" :tour="tour" />

      <table>
        <thead>
          <tr>
            <td>
              <div class="header-space" />
            </td>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>
              <div class="page">
                <div class="page-inner container">
                  <div class="d-flex justify-content-between align-items-center hide-print mb-32">
                    <div v-if="showSupplierCheckbox">
                      <v-checkbox
                        id="my-supplier-checkbox"
                        v-model="showSuuplier"
                        :label="$t('general.shared.showSuppliers')"
                        class="text-nowrap"
                      />
                    </div>
                    <v-button
                      :disabled="downloadingPdf"
                      class="fw-medium text-sm hide-print"
                      size="xs"
                      variant="primary"
                      @click="downloadPDF"
                    >
                      <v-icon icon="download" space="me-8" />

                      <template v-if="downloadingPdf">{{ $t("general.button.generatingPdf") }}</template>
                      <template v-else>{{ $t("general.button.download") }}</template>
                    </v-button>
                  </div>

                  <div class="d-flex flex-column flex-lg-row gap-lg-24 justify-content-between">
                    <the-travel-book-client v-if="!!tour" :tour="tour" />

                    <the-travel-book-agency-billing v-if="!!tour" :tour="tour" />
                  </div>

                  <div v-if="!!featuredImage" class="featured-image mt-32">
                    <v-picture class="d-block">
                      <img :alt="$t('clientTour.featuredImage')" :src="featuredImage.storageUrl" />
                    </v-picture>
                  </div>
                </div>
              </div>

              <div class="page">
                <the-travel-book-resume-days
                  v-if="!!tour"
                  :is-summary="true"
                  :tour-days="tourDays"
                  :show-suuplier="showSuuplier"
                />
              </div>

              <div class="page">
                <the-travel-book-price
                  v-if="!!tour && !!tourTotalPrice"
                  :price-type="priceType"
                  :tour-total-price="tourTotalPrice"
                />

                <the-travel-book-optional-resources v-if="!!tour && optionals.length > 0" :tour-days="optionals" />

                <div v-if="!!tour?.clientTour?.complementaryText" id="page-aditional-information">
                  <div class="page-inner container">
                    <h3 class="h5 mb-12">{{ $t("travelBook.extraField") }}</h3>

                    <div
                      class="terms-container table-container bg-white w-100 rounded-sm p-28"
                      style="box-shadow: 0 1px 0.25rem var(--uq-gray-300)"
                    >
                      <div v-html="tour.clientTour.complementaryText" />
                    </div>
                  </div>
                </div>

                <the-travel-book-terms v-if="!!tour && tour?.clientTour?.translations.length > 0" :tour="tour" />
              </div>
            </td>
          </tr>
        </tbody>

        <tfoot>
          <tr>
            <td>
              <div class="footer-space" />
            </td>
          </tr>
        </tfoot>
      </table>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { onMounted } from "vue";
import axios from "axios";
import { DateTime } from "luxon";
import { VCheckbox } from "@uniqoders/form";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import api from "@/api";
import Tour from "@/api/objects/Tour";
import VTravelBookPageHeader from "@/components/tour/book/parts/VTravelBookPageHeader.vue";
import TheTravelBookResumeDays from "@/components/tour/book/parts/TheTravelBookResumeDays.vue";
import TourDay from "@/api/objects/TourDay";
import TheTravelBookPrice from "@/components/tour/book/parts/TheTravelBookPrice.vue";
import TheTravelBookTerms from "@/components/tour/book/parts/TheTravelBookTerms.vue";
import TheTravelBookClient from "@/components/tour/book/parts/TheTravelBookClient.vue";
import TheTravelBookAgencyBilling from "@/components/tour/book/parts/TheTravelBookAgencyBilling.vue";
import TheTravelBookOptionalResources from "@/components/tour/book/parts/TheTravelBookOptionalResources.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VPicture from "@/components/vendor/basic/picture/VPicture.vue";
import Media from "@/api/objects/Media";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { useRoute } from "vue-router";
import { computed } from "vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  caseId: string;
  tourId: string;
}>();

const route = useRoute();
const { t } = useI18n();

const token = computed(() => route.query.token);
const locale = computed(() => route.query.locale);
const summary = computed(() => (!!route.query.summary && route.query.summary === '1' ? 1 : 0));
const domain = import.meta.env.VITE_APP_API_URL;

const isOptional = computed(() => !!route.query.is_optional && route.query.is_optional === '1');
const optional = computed(() => (isOptional.value ? 1 : 0));
const priceType = computed(() => route.query.price_type || 'tp');

const showSupplierCheckbox = computed(() => {
  const agency = localStorage.getItem('agency');
  const isCollaborator = localStorage.getItem('isCollaborator');
  return !!agency && !!isCollaborator && isCollaborator === 'false';
});

const loadingTravelBook = ref(true);
const downloadingPdf = ref(false);
const show404Error = ref(false);

const tour = ref({} as Tour);
const tourDays = ref<TourDay[]>([]);
const tourTotalPrice = ref("0");
const optionals = ref<TourDay[]>([]);
const featuredImage = ref<Media | null>(null);
const tourDaysDates = ref<Record<string, string | number>[]>([]);
const showSuuplier = ref(false);

onMounted(async () => {
  if (token.value) {
    let tourData: Tour | any = {};

    const params = {
      token: token.value,
      locale: locale.value,
      summary: summary.value,
      price_type: priceType,
      is_optional: optional,
    };

    try {
      tour.value = await api.travelBook.simple(Number(props.caseId), Number(props.tourId), params);
      tourData = await api.travelBook.dataForQuote(Number(props.caseId), Number(props.tourId), params);

      tourDays.value = tourData.tourDays;

      mapTourDays();

      let dayNumber = 0;
      tourDaysDates.value = tourDays.value.map((tourDay: any) => {
        dayNumber += 1;
        return {
          day: dayNumber,
          date: tourDay.date,
        };
      });

      if (isOptional.value) {
        optionals.value = await api.travelBook.optional(Number(props.caseId), Number(props.tourId), params);
        setOptionalToTourDays();

        optionals.value.forEach((optional: any) => {
          const dayDate = tourDaysDates.value.find((tourDayDate) => tourDayDate.date === optional.date);
          if (dayDate) optional.day = dayDate.day;
        });
      }

      document.title = tourData.name;
      featuredImage.value = tourData.featuredImage;

      tourTotalPrice.value =
        tourData.quotePrice?.toString().replace('.', ',') ||
        tourData.grossPrice?.toString().replace('.', ',');

      loadingTravelBook.value = false;
    } catch (e) {
      console.error(e);
      loadingTravelBook.value = false;
      show404Error.value = true;
    }
  } else {
    show404Error.value = true;
  }
});

const mapTourDays = () => {
  const tourDaysItemsHotelCache: Record<string, any>[] = [];

  tourDays.value.forEach((tourDay) => {
    if (!!tourDay.items && tourDay.items.length > 0) {
      tourDay.items.forEach((tourDayItem) => {
        if (tourDayItem.type?.toLowerCase() === 'hotel' && !tourDayItem.disabled) {
          if (!!tourDayItem.resources && tourDayItem.resources.length > 0) {
            tourDayItem.resources.forEach((resource: any) => {
              if (!resource.isOptional) {
                let firstDay = DateTime.fromISO(tourDay.date).plus({ days: 1 });
                const lastDay = DateTime.fromISO(resource.endDate);

                while (firstDay < lastDay) {
                  const tourDayItemResource = {
                    id: resource.id,
                    name: resource.resource.name,
                    currentDay: firstDay.toFormat('yyyy-MM-dd'),
                    type: tourDayItem.type,
                    disabled: true,
                  };

                  tourDaysItemsHotelCache.push(tourDayItemResource);
                  firstDay = firstDay.plus({ days: 1 });
                }
              }
            });
          }
        }
      });
    }
  });

  tourDaysItemsHotelCache.forEach((tourDayItemHotel) => {
    const tourDayCheckOut = tourDays.value.find(
      (tourDayAux) =>
        DateTime.fromISO(tourDayAux.date).toFormat('yyyy-MM-dd') === tourDayItemHotel.currentDay
    );

    if (tourDayCheckOut) {
      if (!tourDayCheckOut.disabledItemResources) {
        tourDayCheckOut.disabledItemResources = [];
      }
      tourDayCheckOut.disabledItemResources.push(tourDayItemHotel);
    }
  });
}

const setOptionalToTourDays = () => {
  optionals.value.forEach((tourDay) => {
    const foundedTourDay = tourDays.value.find(
      (tourDayAux) => tourDayAux.date === tourDay.date
    );

    if (foundedTourDay) {
      if (!foundedTourDay.optionalItemResources) {
        foundedTourDay.optionalItemResources = [];
      }

      tourDay.items.forEach((tourDayItem) => {
        tourDayItem.resources.forEach((resource: any) => {
          const baseTourDayItemResource = {
            id: resource.id,
            name: resource.resource.name,
            currentDay: DateTime.fromISO(tourDay.date).toFormat('yyyy-MM-dd'),
            type: tourDayItem.type,
          };

          if (tourDayItem.type !== 'hotel') {
            foundedTourDay.optionalItemResources.push({ ...baseTourDayItemResource });
          } else {
            let firstDay = DateTime.fromISO(tourDay.date);
            const lastDay = DateTime.fromISO(resource.endDate);

            while (firstDay < lastDay) {
              const currentDay = firstDay.toFormat('yyyy-MM-dd');
              const resourceClone = {
                ...baseTourDayItemResource,
                currentDay,
              };

              const foundedDay = tourDays.value.find(
                (dayAux) =>
                  DateTime.fromISO(dayAux.date).toFormat('yyyy-MM-dd') === currentDay
              );

              if (foundedDay) {
                if (!foundedDay.optionalItemResources) {
                  foundedDay.optionalItemResources = [];
                }
                foundedDay.optionalItemResources.push(resourceClone);
              }

              firstDay = firstDay.plus({ days: 1 });
            }
          }
        });
      });
    }
  });
}

const showToast = (message: string) => {
  (window as any).$toast?.error?.(message);
};

const downloadPDF = async () => {
  try {
    downloadingPdf.value = true;

    const params = {
      token: token.value,
      locale: locale.value,
      summary: summary.value,
      price_type: priceType.value,
      is_optional: optional.value,
    };

    const response = await axios({
      url: `/api/case/${props.caseId}/tour/${props.tourId}/preview/download`,
      baseURL: domain.value,
      params,
      responseType: 'blob',
      withCredentials: true,
    });

    const contentDisposition = response.headers['content-disposition'];
    let filename = tour.value.name;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch.length > 1) {
        filename = filenameMatch[1];
      }
    }

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', `quote-${filename}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (e) {
    console.error(e);
    showToast(t('travelBook.downloadErrorText'));
  } finally {
    downloadingPdf.value = false;
  }
};
</script>

<style lang="scss" scoped>
.hide-print {
  @media print {
    display: none;
  }
}

#generated-travel-book {
  .error-container {
    .title {
      font-size: 5rem;
    }
  }

  * {
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  table {
    width: 100%;
  }

  .header-space {
    position: relative;
    z-index: 1;

    @media print {
      height: 7.375rem;
    }
  }

  .footer-space {
    position: relative;
    z-index: 1;
  }

  @page {
    size: A4;
    margin: 1.5cm;
  }

  @media print {
    thead {
      display: table-header-group;
    }

    tfoot {
      display: table-footer-group;
    }
  }

  .page {
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: 100vw;

    @media print {
      page-break-before: always;
    }

    :deep(.page-inner) {
      padding-inline: 2rem;
      margin: 0 auto;
      width: 100%;

      @media only screen {
        padding-block: 2rem;
      }

      @media print {
        max-width: 58.125rem !important;
        padding-block: 1rem;
      }
    }
  }

  .featured-image {
    :deep(img) {
      height: 100%;
      width: 100%;
      max-height: 18rem;
      object-fit: contain;
    }
  }
}
</style>
